import {DateFilterItem, DateFilterItemDefinition, todayPlusFuture, FeatureFilter, getFilterItems} from '@mc/react-mapio';

/**
 * Helper pro zjištění, zda je DateFilterItemDefinition vlastně enum hodnota nebo custom objekt.
 */
function getItemId(item: DateFilterItemDefinition): string {
    return typeof item === 'string' ? item : item.id;
}

/**
 * Helper pro porovnání dvou dat (ignoruje čas).
 */
function isSameDay(date1: Date, date2: Date): boolean {
    const d1 = new Date(date1);
    d1.setHours(0, 0, 0, 0);
    const d2 = new Date(date2);
    d2.setHours(0, 0, 0, 0);
    return d1.getTime() === d2.getTime();
}

/**
 * Formátuje datum do čitelné podoby
 */
function formatDate(date: Date | string | null): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('cs-CZ', {day: 'numeric', month: 'numeric', year: 'numeric'});
}

/**
 * Výchozí období, pokud ho URL neurčí jinak.
 *
 * Zvoleno v MCW-18757 (do té doby to bylo "Neomezeně") - veřejná mapa má ve výchozím stavu
 * zobrazovat jen objekty platné k aktuálnímu dni a dál do budoucnosti.
 */
export const DEFAULT_DATE_FILTER_ITEM = DateFilterItem.TODAY_AND_FUTURE;

/**
 * Vytvoří label pro aktuálně vybraný filtr podle dateFilterRange
 * Pro CUSTOM_RANGE a CUSTOM_DATE zobrazí rozsah/datum místo názvu
 */
export function getDateFilterLabel(dateFilterRange: FeatureFilter | null, dateFilterItems: DateFilterItemDefinition[]): string {
    // Nejdřív zjistíme, který DateFilterItem odpovídá aktuálnímu dateFilterRange
    const filterItemId = deriveDateFilterItemFromRange(dateFilterRange, dateFilterItems);

    if (!filterItemId) {
        return 'Vyberte filtr';
    }

    // Pro CUSTOM_DATE zobrazíme jen jedno datum
    if (filterItemId === DateFilterItem.CUSTOM_DATE && dateFilterRange) {
        const {dateFrom, date} = dateFilterRange;

        // CUSTOM_DATE může mít buď 'date' nebo dateFrom
        if (date && !Array.isArray(date)) {
            return formatDate(date);
        }
        if (dateFrom) {
            return formatDate(dateFrom);
        }
    }

    // Pro CUSTOM_RANGE zobrazíme rozsah datumů místo textu "Vlastní období"
    if (filterItemId === DateFilterItem.CUSTOM_RANGE && dateFilterRange) {
        const {dateFrom, dateTo} = dateFilterRange;

        if (dateFrom && dateTo) {
            // Pokud jsou stejné, zobrazíme jen jedno datum
            if (isSameDay(new Date(dateFrom), new Date(dateTo))) {
                return formatDate(dateFrom);
            }
            // Jinak zobrazíme rozsah
            return `${formatDate(dateFrom)} - ${formatDate(dateTo)}`;
        }

        // Pouze dateFrom nebo dateTo
        if (dateFrom && !dateTo) {
            return `${formatDate(dateFrom)} - budoucnost`;
        }
        if (!dateFrom && dateTo) {
            return `minulost - ${formatDate(dateTo)}`;
        }
    }

    // Pro ostatní položky vrátíme jejich název - u předdefinovaných období ho zná engine,
    // u custom položek přišel z hostující aplikace. Stejný zdroj používá i nabídka filtru,
    // takže se popisek tlačítka a text v menu nerozejdou.
    const item = getFilterItems(dateFilterItems).find(filterItem => getItemId(filterItem) === filterItemId);
    return (typeof item === 'object' && item.name) || filterItemId;
}

/**
 * Odvozuje DateFilterItem ID z FeatureFilter hodnot.
 * Používá se pro určení aktivní položky v UI bez potřeby samostatného state.
 * @param dateFilterRange - objekt s dateFrom a dateTo
 * @param dateFilterItems - dostupné položky filtru (pro kontrolu, které jsou povolené)
 * @returns odpovídající ID filtru nebo null pokud nelze určit
 */
export function deriveDateFilterItemFromRange(dateFilterRange: FeatureFilter | null, dateFilterItems: DateFilterItemDefinition[]): string | null {
    if (!dateFilterRange) {
        // Pokud není žádný range, vraťme default (pokud je v dostupných položkách)
        const todayAndFuture = dateFilterItems.find(item => getItemId(item) === DateFilterItem.TODAY_AND_FUTURE);
        return todayAndFuture ? getItemId(todayAndFuture) : dateFilterItems[0] ? getItemId(dateFilterItems[0]) : null;
    }

    const {dateFrom, dateTo, date} = dateFilterRange;

    // CUSTOM_DATE: pokud je přítomna property 'date' (bez dateFrom/dateTo)
    if (date && !Array.isArray(date) && !dateFrom && !dateTo) {
        const customDate = dateFilterItems.find(item => getItemId(item) === DateFilterItem.CUSTOM_DATE);
        if (customDate) return getItemId(customDate);
        // Fallback na CUSTOM_RANGE pokud není CUSTOM_DATE v dostupných položkách
        return DateFilterItem.CUSTOM_RANGE;
    }

    // Pokud jsou obě hodnoty null (a není 'date'), jde o UNLIMITED
    if (!dateFrom && !dateTo && !date) {
        const unlimited = dateFilterItems.find(item => getItemId(item) === DateFilterItem.UNLIMITED);
        return unlimited ? getItemId(unlimited) : null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Pokud je dateFrom dnešní datum a dateTo je null, jde o TODAY_AND_FUTURE
    if (dateFrom && !dateTo) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);

        if (from.getTime() === today.getTime()) {
            const todayAndFuture = dateFilterItems.find(item => getItemId(item) === DateFilterItem.TODAY_AND_FUTURE);
            if (todayAndFuture) return getItemId(todayAndFuture);
        }

        // Jinak je to CUSTOM_RANGE (datum od - budoucnost)
        return DateFilterItem.CUSTOM_RANGE;
    }

    // Pokud je dateTo bez dateFrom, je to také CUSTOM_RANGE (minulost - datum do)
    if (!dateFrom && dateTo) {
        return DateFilterItem.CUSTOM_RANGE;
    }

    // Převedeme dateFilterItems na selectItems pomocí getFilterItems z react-mapio
    // To nám dá objekty s vypočítanými hodnotami date ranges
    const selectItems = getFilterItems(dateFilterItems);

    // KLÍČOVÁ ZMĚNA: Projdeme všechny položky a zkusíme najít shodu
    if (dateFrom && dateTo) {
        for (const item of selectItems) {
            // selectItems obsahuje vždy objekty, ne string enum hodnoty
            if (typeof item === 'string') continue;

            // Pro položky s value porovnáme jejich dateFrom/dateTo s aktuálním range
            if (item?.value?.dateFrom && item.value?.dateTo) {
                const itemFrom = new Date(item.value.dateFrom);
                const itemTo = new Date(item.value.dateTo);
                const rangeFrom = new Date(dateFrom);
                const rangeTo = new Date(dateTo);

                // Porovnáme datumy (ignorujeme čas)
                if (isSameDay(itemFrom, rangeFrom) && isSameDay(itemTo, rangeTo)) {
                    return item.id;
                }
            }
        }

        // Pokud jsme nenašli shodu, zkusíme ještě TODAY (speciální případ)
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);

        const to = new Date(dateTo);
        to.setHours(0, 0, 0, 0);

        // TODAY
        if (from.getTime() === today.getTime() && to.getTime() === today.getTime()) {
            const todayItem = dateFilterItems.find(item => getItemId(item) === DateFilterItem.TODAY);
            return todayItem ? getItemId(todayItem) : DateFilterItem.CUSTOM_RANGE;
        }

        // Pokud jsou dateFrom a dateTo stejné, je to CUSTOM_DATE
        if (isSameDay(from, to)) {
            const customDate = dateFilterItems.find(item => getItemId(item) === DateFilterItem.CUSTOM_DATE);
            if (customDate) return getItemId(customDate);
        }

        // Jinak je to CUSTOM_RANGE
        return DateFilterItem.CUSTOM_RANGE;
    }

    // Pokud je pouze dateFrom nebo dateTo, je to CUSTOM_RANGE
    return DateFilterItem.CUSTOM_RANGE;
}

/**
 * Parsuje query parametry validFrom a validTo a vrací odpovídající DateFilterItem a FeatureFilter
 * @param validFrom - datum ve formátu YYYY-MM-DD
 * @param validTo - datum ve formátu YYYY-MM-DD
 * @returns objekt s dateFilter a dateFilterRange, nebo null pokud nejsou query parametry platné
 */
export function parseDateFromQueryParams(
    validFrom?: string,
    validTo?: string,
): {
    dateFilter: DateFilterItem;
    dateFilterRange: {dateFrom: string | null; dateTo: string | null} | null;
} | null {
    if (!validFrom && !validTo) {
        return null;
    }

    // Validace formátu data (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (validFrom && !dateRegex.test(validFrom)) {
        console.warn('Neplatný formát validFrom:', validFrom);
        return null;
    }

    if (validTo && !dateRegex.test(validTo)) {
        console.warn('Neplatný formát validTo:', validTo);
        return null;
    }

    // Pokud jsou oba parametry zadané, používáme CUSTOM_RANGE
    if (validFrom && validTo) {
        return {
            dateFilter: DateFilterItem.CUSTOM_RANGE,
            dateFilterRange: {
                dateFrom: validFrom,
                dateTo: validTo,
            },
        };
    }

    // Pokud je jen validFrom (bez validTo), používáme CUSTOM_RANGE s dateTo: null (do budoucnosti)
    if (validFrom && !validTo) {
        return {
            dateFilter: DateFilterItem.CUSTOM_RANGE,
            dateFilterRange: {
                dateFrom: validFrom,
                dateTo: null,
            },
        };
    }

    // Pokud je jen validTo (bez validFrom), používáme také CUSTOM_RANGE s neomezeným začátkem
    if (!validFrom && validTo) {
        return {
            dateFilter: DateFilterItem.CUSTOM_RANGE,
            dateFilterRange: {
                dateFrom: null,
                dateTo: validTo,
            },
        };
    }

    return null;
}

/**
 * Vrátí rozsah období odpovídající id položky filtru.
 *
 * Rozsahy předdefinovaných období bere z enginu, ať mapa i nabídka filtru počítají se stejnými
 * datumy; custom položky poslané hostující aplikací mají rozsah přímo v definici.
 *
 * @returns Rozsah, nebo `null` u položek bez rozsahu (CUSTOM_DATE, CUSTOM_RANGE - ty zadává
 *          uživatel sám) a u neznámého id.
 */
function getRangeForItemId(itemId: string, dateFilterItems?: DateFilterItemDefinition[]): FeatureFilter | null {
    const customItem = dateFilterItems?.find(item => typeof item === 'object' && item.id === itemId);
    if (customItem && typeof customItem === 'object') {
        return customItem.value
            ? {
                  dateFrom: customItem.value.dateFrom ? new Date(customItem.value.dateFrom) : null,
                  dateTo: customItem.value.dateTo ? new Date(customItem.value.dateTo) : null,
              }
            : null;
    }

    if (!Object.values(DateFilterItem).includes(itemId as DateFilterItem)) {
        return null;
    }
    const [item] = getFilterItems([itemId as DateFilterItem]);
    const value = typeof item === 'object' ? item.value : null;
    return value ? {dateFrom: value.dateFrom, dateTo: value.dateTo} : null;
}

/**
 * Vrátí id období, které se má použít jako výchozí.
 *
 * Hodnotu z URL zahodíme, pokud z ní rozsah odvodit nelze (CUSTOM_DATE, CUSTOM_RANGE, neznámé id) -
 * pro takový případ platí výchozí období. Mapa ho dostává jako query parametr `defaultDateFilter`,
 * takže musí rozhodnout stejně jako popisek filtru, jinak by tlačítko psalo něco jiného, než co je
 * v mapě vidět.
 */
export function getDefaultDateFilterId(queryParams: Record<string, string>, dateFilterItems?: DateFilterItemDefinition[]): string {
    const {defaultDateFilter} = queryParams;
    if (defaultDateFilter && getRangeForItemId(defaultDateFilter, dateFilterItems)) {
        return defaultDateFilter;
    }
    return DEFAULT_DATE_FILTER_ITEM;
}

/**
 * Vyhodnotí výchozí datumový filtr z query parametrů URL.
 *
 * Priorita: konkrétní `validFrom`/`validTo` → pojmenované období z `defaultDateFilter`
 * → výchozí období.
 */
export function getDefaultDateFilterFromQueryParams(queryParams: Record<string, string>, dateFilterItems?: DateFilterItemDefinition[]): FeatureFilter {
    const {validFrom, validTo} = queryParams;
    const parsed = parseDateFromQueryParams(validFrom, validTo);
    if (parsed) {
        const dateFrom = parsed.dateFilterRange?.dateFrom ? new Date(parsed.dateFilterRange.dateFrom) : null;
        const dateTo = parsed.dateFilterRange?.dateTo ? new Date(parsed.dateFilterRange.dateTo) : null;
        return {dateFrom, dateTo};
    }

    const itemId = getDefaultDateFilterId(queryParams, dateFilterItems);
    return getRangeForItemId(itemId, dateFilterItems) ?? todayPlusFuture();
}
